<template>
  <w-modal
    v-model="show"
    class="modal-big"
    custom-content-class="modal--overflow-hidden"
    :show-close-button="false"
    :submit-button="submitButtonTitle"
    :close-on-submit="false"
    :disable-submit-button="isFileUploading"
    :hide-on-overlay="false"
    :hide-on-esc="false"
    :title="title"
    @close="close"
    @submit="filterAndClose"
  >
    <div class="row row--fixed-height">
      <div class="col-3 px-5 overflow-auto column--fixed-height">
        <input
          class="form-control mb-4"
          placeholder="Szukaj filtrów..."
          title="Wyszukiwarka filtrów"
          type="search"
          @input="search"
        />

        <div
          v-for="(filter, index) in searchedFilters"
          :key="'filter_' + index"
          class="pointer p-2"
          tabindex="0"
          :class="{ 'selected-filter': filterTitle === filter.name, 'font-weight-bold': filterBold(filter) }"
          @click="openFilter(filter)"
          @keydown="keydownHandler('openFilter', filter)"
        >
          {{ filterCount(filter) }}
        </div>
      </div>

      <div class="col-9 px-5 overflow-auto column--fixed-height">
        <h1 class="mb-3">{{ filterTitle }}</h1>

        <schema-form
          v-if="show && loadedSchema"
          v-bind="{ ...$attrs, ...$props }"
          :filter="selectedFilter"
          :schema="$schema"
          :template-name="schemaName"
          :title="filterTitle"
          @update="save"
        />
      </div>
    </div>
  </w-modal>
</template>

<script>
import { mapState } from 'vuex';

import SchemaForm from '@/components/forms/SchemaForm';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Storage } from '@/enums/storage.enum';
import { formMixin } from '@/mixins/form';
import { formRenderContainerMixin } from '@/mixins/formRenderContainer';
import { keyboardMixin } from '@/mixins/keyboard';
import { modalMixin } from '@/mixins/modal';
import { notificationsMixin } from '@/mixins/notifications';
import { getFilters } from '@/utils/document';

// @vuese
// Modal formularza generowanego na podstawie schemy
// @group Modals
export default {
  components: { SchemaForm },

  mixins: [formMixin, formRenderContainerMixin, keyboardMixin, modalMixin, notificationsMixin],

  external: {},

  props: {
    // @vuese
    // Lista dostępnych filtrów
    // WAŻNE: Schema filtrów nie może używać refów!
    availableFilters: { type: Array, required: true },

    // Encja doklejana do nazwy pola w localStorage
    entityId: { type: [String, Number], required: false },

    // @vuese
    // Tytuł modala
    title: { type: String, required: false }
  },

  data() {
    return {
      filters: undefined,
      searchedFilters: [...this.availableFilters],
      selectedFilter: undefined
    };
  },

  computed: {
    ...mapState({
      submitButtonSetting(state) {
        return state.setting.settings[PublicSettings.SUBMIT_BUTTON_TEXT];
      },
      isFileUploading(state) {
        return state.media.isUploading;
      }
    }),

    submitButtonTitle() {
      return this.submitButtonSetting?.value || 'Wykonaj';
    },

    formData() {
      if (this.selectedFilter) {
        return this.filters?.[this.selectedFilter.field] || this.getEmptyModel(this.schema);
      }
      return {};
    },

    filterTitle() {
      return this.selectedFilter?.name;
    }
  },

  watch: {
    show() {
      this.filters = getFilters(this.entityId);
      this.openFilter(this.availableFilters[0]);
    }
  },

  methods: {
    // @vuese
    // Zapisuje dane z formularza
    save() {
      if (this.$schema.$validateAll().length) {
        return this.notifyError(Errors.FORM_CORRECT_ERRORS);
      }
      this.saveFilters(this.$schema.$getData());
    },

    // @vuese
    // Zapisuje wybrane filtry
    saveFilters(fieldFilters) {
      this.filters = { ...this.filters, [this.selectedFilter.field]: this.filtersOrUndefined(fieldFilters) };
      this.updateFilters();
    },

    // @vuese
    // Jeżeli nic nie zostało wybrane na modalu, to filtry powinny być puste
    filtersOrUndefined(filters) {
      const hasSelectedFilters =
        !!filters &&
        Object.values(filters).filter(
          filterValue =>
            filterValue !== undefined &&
            filterValue !== null &&
            ((!Array.isArray(filterValue) && typeof filterValue !== 'string') || filterValue.length)
        ).length;

      if (hasSelectedFilters) {
        return filters;
      }
      return undefined;
    },

    // @vuese
    // Zapisuje filtry do storage
    updateFilters() {
      localStorage.setItem(Storage.FILTERS + (this.entityId || ''), JSON.stringify(this.filters));
    },

    // @vuese
    // Ustawia czysty model danych
    setData() {
      if (this.formData && this.$schema) {
        this.$schema.$setData(this.formData, undefined, true);
      }
    },

    // @vuese
    // Wyszukuje filtry
    search(evt) {
      const search = evt.target.value;
      if (!search) {
        this.searchedFilters = [...this.availableFilters];
        return;
      }
      const re = new RegExp(search, 'i');
      this.searchedFilters = this.availableFilters.filter(filter => filter.name.match(re));
    },

    // @vuese
    // Wyświetla filtr
    openFilter(filter) {
      this.loadedSchema = false;
      this.$store.dispatch('schema/get', filter.schemaId).then(() => {
        this.selectedFilter = filter;
        this.loadSchema();
      });
    },

    // @vuese
    // Zwraca pusty model schemy
    // WAŻNE: Schema filtrów nie może używać refów!
    getEmptyModel(schema) {
      return Object.keys(schema.properties).reduce(
        (data, property) => ({ ...data, [property]: this.getDefaultValue(schema, property) }),
        {}
      );
    },

    // @vuese
    // Zwraca domyślną wartość dla danego pola
    getDefaultValue(schema, propertyName) {
      const property = schema.properties[propertyName];
      if (property.type === 'array') {
        return [];
      }
      if (property.type === 'object') {
        return this.getEmptyModel(property);
      }
      return null;
    },

    // @vuese
    // Zamyka modal i emituje obiekt z wybranymi filtrami
    filterAndClose() {
      this.close();
      this.$emit('filter', this.filters);
    },

    // @vuese
    // Liczenie filtrów
    filterCount(filter) {
      if (this.filters?.[filter.field]?.$in) {
        return filter.name + ` (${this.filters[filter.field].$in.length})`;
      } else {
        return filter.name;
      }
    },

    // @vuese
    // Sprawdzanie czy są wybrane filtry
    filterBold(filter) {
      return !!this.filters?.[filter.field];
    }
  }
};
</script>
